import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Ruler, Info } from 'phosphor-react';
import { ftToM, mToFt } from '../utils/units';

export const BridgeLengthInput = ({ length, onLengthChange, units = 'imperial' }) => {
  const [inputValue, setInputValue] = useState(length.toString());
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (units === 'metric') {
      setInputValue(ftToM(length).toFixed(2));
    } else {
      setInputValue(length.toString());
    }
  }, [length, units]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setIsValid(false);
      return;
    }
    if (units === 'imperial') {
      if (numValue < 10 || numValue > 500) setIsValid(false);
      else {
        setIsValid(true);
        onLengthChange(numValue);
      }
    } else {
      // metric: 10ft-500ft equivalence in meters
      const min = 10 * 0.3048;
      const max = 500 * 0.3048;
      if (numValue < min || numValue > max) setIsValid(false);
      else {
        setIsValid(true);
        onLengthChange(mToFt(numValue));
      }
    }
  };

  const presetFeet = [25, 50, 75, 100, 150, 200];
  const presetMeters = [10, 12, 15, 20, 25, 30];
  const presets = units === 'metric' ? presetMeters : presetFeet;

  return (
    <Card className="border-slate-600 bg-slate-800 shadow-xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-slate-100 flex items-center gap-2">
          <Ruler className="w-5 h-5 text-blue-400" />
          Bridge Length
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="length" className="text-slate-300">
            {units === 'metric' ? 'Total Length (meters)' : 'Total Length (feet)'}
          </Label>
          <Input
            id="length"
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            className={`bg-slate-700 border-slate-600 text-slate-200 ${!isValid ? 'border-red-500 ring-red-500' : 'focus:border-blue-400 focus:ring-blue-400'}`}
            placeholder={units === 'metric' ? 'Enter length in meters' : 'Enter length in feet'}
            min={units === 'metric' ? (10 * 0.3048).toFixed(2) : 10}
            max={units === 'metric' ? (500 * 0.3048).toFixed(2) : 500}
            step={units === 'metric' ? '0.01' : '1'}
          />
          {!isValid && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <Info className="w-4 h-4" />
              {units === 'metric' ? 'Length must be between 3.05 and 152.40 meters' : 'Length must be between 10 and 500 feet'}
            </div>
          )}
          {isValid && length && (
            <div className="text-slate-400 text-sm">
              Current length: {units === 'metric' ? `${ftToM(length).toFixed(2)} m` : `${length} ft`}
            </div>
          )}
        </div>
        <div>
          <Label className="text-slate-300 text-sm mb-2 block">Quick Select ({units === 'metric' ? 'm' : 'ft'})</Label>
          <div className="grid grid-cols-3 gap-2">
            {presets.map((preset, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                className={`h-8 border-slate-600 text-slate-300 hover:bg-slate-600 ${
                  (units === 'metric' ? Math.abs(ftToM(length) - preset) < 1e-2 : length === preset) ? 'bg-blue-600 text-white border-blue-500' : 'bg-slate-700'
                }`}
                onClick={() => {
                  const ftVal = units === 'metric' ? mToFt(preset) : preset;
                  onLengthChange(ftVal);
                }}
              >
                {units === 'metric' ? preset.toFixed(2) : preset}{units === 'metric' ? ' m' : "'"}
              </Button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};